import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from '../components/nav';
import { facsimileUrl, loadFacsimile } from '../data/load';
import type { FacsimilePage } from '../data/types';

const KIND_LABEL: Record<string, string> = {
  text: 'Páginas de texto',
  plate: 'Láminas',
  volume: 'Cubiertas, portada e índices',
  blank: 'Guardas, blancos y objetivos del escaneo',
};

/** Recorta un pie largo: en la rejilla solo cabe una línea. */
const short = (s: string | null) => (s && s.length > 42 ? `${s.slice(0, 40)}…` : s) || null;

/** El volumen completo (fase F): las 778 hojas del ejemplar escaneado, no solo las de los capítulos
 *  editados. Aquí se ven la cubierta, la portada, el índice general y la plantilla de láminas, que
 *  no pertenecen a ningún capítulo y que el lector, por tanto, nunca enseña. */
export function Volume() {
  const [params, setParams] = useSearchParams();
  const [pages, setPages] = useState<FacsimilePage[] | null | undefined>(undefined);
  const [kind, setKind] = useState<string>('');

  useEffect(() => { loadFacsimile().then((f) => setPages(f?.pages ?? null)); }, []);

  const leaf = Number(params.get('hoja') ?? NaN);
  const shown = useMemo(() => (pages ?? []).filter((p) => !kind || p.kind === kind), [pages, kind]);
  const open = useMemo(() => (pages ?? []).find((p) => p.leaf === leaf), [pages, leaf]);
  const pos = shown.findIndex((p) => p.leaf === leaf);

  const goto = (l: number | null) => setParams((prev) => {
    const n = new URLSearchParams(prev);
    if (l === null) n.delete('hoja'); else n.set('hoja', String(l));
    return n;
  }, { replace: true });

  if (pages === undefined) return <div className="page"><h1>El volumen</h1><p role="status">Cargando el facsímil…</p></div>;
  if (pages === null) {
    return (
      <div className="page">
        <h1>El volumen</h1>
        <p>El facsímil no está disponible en esta instalación. Se genera con <code>python3 etl/14_build_facsimile.py --volume</code>.</p>
      </div>
    );
  }

  return (
    <div className="page volume">
      <h1>El volumen</h1>
      <p>
        Las {pages.length} hojas del ejemplar que digitalizó Internet Archive, tal cual: la encuadernación, la portada de 1884,
        las {pages.filter((p) => p.kind === 'plate').length} láminas, el índice general y la plantilla que decía al encuadernador dónde colocar cada grabado.
        La edición transcribe los capítulos; esto es el libro como objeto.
      </p>
      <p className="note">
        Para leer, vete a <Link to="/leer">Leer</Link>: allí cada pasaje trae su propia página original al lado de la transcripción.
        Estas imágenes son de dominio público (Quadrado, 1884); la digitalización es de Internet Archive, del ejemplar del Getty Research Institute.
      </p>

      <fieldset className="filters">
        <legend className="visually-hidden">Filtrar hojas</legend>
        <label>Tipo de hoja
          <select value={kind} onChange={(e) => { setKind(e.target.value); goto(null); }}>
            <option value="">Todas ({pages.length})</option>
            {Object.entries(KIND_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v} ({pages.filter((p) => p.kind === k).length})</option>
            ))}
          </select>
        </label>
      </fieldset>

      {open && (
        <section className="volume__viewer" aria-label={`Hoja ${open.leaf}`}>
          <div className="facsimile__bar">
            <button type="button" onClick={() => goto(shown[pos - 1]?.leaf ?? open.leaf)} disabled={pos <= 0} aria-label="Hoja anterior">←</button>
            <span className="facsimile__label" aria-live="polite">
              {open.caption ?? (open.printedPage ? `Página ${open.printedPage}` : KIND_LABEL[open.kind])} <span className="muted">· hoja {open.leaf}</span>
            </span>
            <button type="button" onClick={() => goto(shown[pos + 1]?.leaf ?? open.leaf)} disabled={pos < 0 || pos >= shown.length - 1} aria-label="Hoja siguiente">→</button>
            <button type="button" onClick={() => goto(null)}>Cerrar</button>
          </div>
          <img className="volume__big" src={facsimileUrl(open.file)} width={open.width} height={open.height}
               alt={`Facsímil de la hoja ${open.leaf} de la edición de 1884${open.caption ? `: ${open.caption}` : ''}. Imagen del ejemplar escaneado; el texto transcrito está en la vista Leer.`} />
        </section>
      )}

      <ul className="volume__grid">
        {shown.map((p) => (
          <li key={p.leaf}>
            <button type="button" aria-pressed={p.leaf === leaf} onClick={() => goto(p.leaf)}>
              <img src={facsimileUrl(p.file, 'thumbs')} loading="lazy" width={150} height={Math.round(p.height * 150 / p.width)}
                   alt={`Miniatura de la hoja ${p.leaf}`} />
              <span className="volume__cap">
                {p.printedPage ? `p. ${p.printedPage}` : short(p.caption) ?? KIND_LABEL[p.kind].split(',')[0]}
                <span className="muted"> · h. {p.leaf}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
